import { useState, useCallback } from "react";
import Modal from "components/ui/modals";
import Input from "components/ui/Input";
import { useCreateTemplate } from "http-hooks/ordtemplate/useOrdTemplate";

export default function SaveTemplateModal({ isOpen, onClose, vendorId, columnOrderItems }) {
  const [templateName, setTemplateName] = useState("");
  const { mutateAsync: createMutate, isPending: isSaving } = useCreateTemplate();

  const handleSave = useCallback(async () => {
    if (!templateName.trim()) return;

    const items = [];
    columnOrderItems.forEach((row) => {
      if (!row.item || !row.quantities) return;
      row.quantities.forEach((q) => {
        const qty = parseInt(q.qty) || 0;
        if (qty > 0 && q.agitemdtlId) {
          items.push({
            itmid: q.agitemdtlId,
            qty,
          });
        }
      });
    });

    if (items.length === 0) return;

    try {
      await createMutate({
        name: templateName.trim(),
        vendorId: vendorId || "",
        items,
      });
      setTemplateName("");
      onClose();
    } catch (error) {
      console.error("Failed to save template:", error);
    }
  }, [templateName, columnOrderItems, vendorId, createMutate, onClose]);

  const itemCount = columnOrderItems.filter(
    (row) => row.item && row.quantities?.some((q) => parseInt(q.qty) > 0)
  ).length;

  return (
    <Modal
      open={isOpen}
      setOpen={() => onClose()}
      maxWidth="md"
      header={
        <div>
          <h3 className="text-lg font-semibold leading-6 text-gray-900">Save Order Template</h3>
          <p className="mt-0.5 text-sm text-gray-500">Save the current order items as a reusable template.</p>
        </div>
      }
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!templateName.trim() || itemCount === 0 || isSaving}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save Template"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="Template Name *"
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Enter template name"
          maxLength={50}
        />

        <div className="rounded-md bg-gray-50 px-4 py-3">
          <p className="text-sm text-gray-700">
            <span className="font-medium">{itemCount}</span> item(s) with quantities will be saved to this template.
          </p>
        </div>

        {itemCount === 0 && (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3">
            <p className="text-sm text-yellow-700">
              No items with quantities to save. Add items with quantities first.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
